import { useState, useMemo, SyntheticEvent } from 'react';
import { Autocomplete, TextField, List, ListItem, ListItemButton } from '@mui/material';
import { Clear, LocationOn, KeyboardArrowDown } from '@mui/icons-material';
import { useStore } from 'providers';
import { Choice, OnboardingSlideProps } from 'models';
import { RestaurantLocationName, StyledBox } from './RestaurantLocation.styles';

const RestaurantLocation = ({
  onboardingData: { surveyAnswers, buttonState },
  setOnboardingData,
}: OnboardingSlideProps) => {
  const { store } = useStore();
  const { surveyQuestions } = store;

  const {
    favouriteRestaurant: { questionID, choices },
  } = surveyQuestions || {};

  const [chosenLocation, setChosenLocation] = useState<Choice | null>(null);
  const [inputValue, setInputValue] = useState<string>('');

  const displayLocations = useMemo(() => {
    const locationsList = choices?.map(({ choiceID, choiceLabel }) => ({
      choiceID,
      choiceLabel: choiceLabel.toLowerCase(),
    }));

    return locationsList || [];
  }, [choices]);

  const setSurveyAnswers = (location: Choice | null) => {
    const choiceIDs = location ? [location.choiceID] : [];
    const answer3 = { questionID, choiceIDs };

    setOnboardingData({
      type: 'setSurveyAnswers',
      data: { surveyAnswers: { ...surveyAnswers, answer3 } },
    });
  };

  const handleAutocompleteChange = (_: SyntheticEvent, location: Choice | null) => {
    setChosenLocation(location);
    setSurveyAnswers(location);
  };

  const handleOnInputChange = (_: SyntheticEvent, value: string) => {
    const updatedButtonState = { ...buttonState, location: !value };
    setInputValue(value);
    setOnboardingData({
      type: 'setButtonState',
      data: { buttonState: updatedButtonState },
    });
  };

  const handleOnClear = () => {
    setChosenLocation(null);
    setSurveyAnswers(null);
  };

  return (
    <StyledBox>
      <Autocomplete
        aria-label="Restaurant Search"
        fullWidth
        popupIcon={<KeyboardArrowDown />}
        options={displayLocations}
        getOptionLabel={(option) => option.choiceLabel}
        renderInput={(params) => <TextField {...params} variant="filled" label="Type a location" />}
        onChange={handleAutocompleteChange}
        value={chosenLocation}
        inputValue={inputValue}
        onInputChange={handleOnInputChange}
      />

      {chosenLocation && (
        <List>
          <ListItem
            key={chosenLocation.choiceLabel}
            secondaryAction={<Clear onClick={handleOnClear} />}
            disablePadding
          >
            <ListItemButton>
              <LocationOn />
              <RestaurantLocationName primary={chosenLocation.choiceLabel} />
            </ListItemButton>
          </ListItem>
        </List>
      )}
    </StyledBox>
  );
};

export default RestaurantLocation;
